
import React, { useState, useEffect, useRef } from 'react';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

interface DateInputProps {
  value: string;
  onChange: (isoDate: string | null, age: number) => void;
  error?: string;
  label?: string;
}

export const DateInput: React.FC<DateInputProps> = ({
  value,
  onChange,
  error,
  label = "Data de nascimento"
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [displayValue, setDisplayValue] = useState(() => {
    if (value) {
      // Parse ISO date string directly to DD/MM/YYYY without timezone conversion
      const parts = value.split('-');
      if (parts.length === 3) {
        const [year, month, day] = parts;
        return `${day}/${month}/${year}`;
      }
    }
    return '';
  });

  // Sync displayValue when value prop changes (for loading existing data)
  useEffect(() => {
    if (value) {
      // Parse ISO date string directly to DD/MM/YYYY without timezone conversion
      const parts = value.split('-');
      if (parts.length === 3) {
        const [year, month, day] = parts;
        setDisplayValue(`${day}/${month}/${year}`);
      }
    } else {
      setDisplayValue('');
    }
  }, [value]);

  const calculateAge = (year: number, month: number, day: number): number => {
    const today = new Date();
    let age = today.getFullYear() - year;
    const monthDiff = today.getMonth() + 1 - month; // getMonth() is 0-based
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < day)) {
      age--;
    }
    
    return age;
  };

  const formatDate = (input: string): string => {
    // Remove all non-numeric characters
    const numbers = input.replace(/\D/g, '');
    
    // Apply formatting DD/MM/YYYY
    if (numbers.length <= 2) {
      return numbers;
    } else if (numbers.length <= 4) {
      return `${numbers.slice(0, 2)}/${numbers.slice(2)}`;
    } else {
      return `${numbers.slice(0, 2)}/${numbers.slice(2, 4)}/${numbers.slice(4, 8)}`;
    }
  };

  const validateAndParseDate = (dateStr: string): { isValid: boolean; year?: number; month?: number; day?: number; age?: number; errorMessage?: string } => {
    // Check exact format DD/MM/YYYY
    if (dateStr.length !== 10) {
      return { isValid: false, errorMessage: "Data deve ter o formato DD/MM/YYYY" };
    }

    // Check if format matches DD/MM/YYYY pattern
    const datePattern = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    const match = dateStr.match(datePattern);
    
    if (!match) {
      return { isValid: false, errorMessage: "Data deve ter o formato DD/MM/YYYY" };
    }

    const [, dayStr, monthStr, yearStr] = match;
    const day = parseInt(dayStr, 10);
    const month = parseInt(monthStr, 10);
    const year = parseInt(yearStr, 10);
    
    // Check for valid numeric values
    if (isNaN(day) || isNaN(month) || isNaN(year)) {
      return { isValid: false, errorMessage: "Data contém valores inválidos" };
    }

    // Enhanced validation ranges
    const currentYear = new Date().getFullYear();
    if (day < 1 || day > 31) {
      return { isValid: false, errorMessage: "Dia deve estar entre 01 e 31" };
    }
    if (month < 1 || month > 12) {
      return { isValid: false, errorMessage: "Mês deve estar entre 01 e 12" };
    }
    if (year < 1900 || year > currentYear) {
      return { isValid: false, errorMessage: `Ano deve estar entre 1900 e ${currentYear}` };
    }

    // Validate if it's a real date by creating a Date object and checking consistency
    const testDate = new Date(year, month - 1, day);
    if (testDate.getDate() !== day || testDate.getMonth() !== month - 1 || testDate.getFullYear() !== year) {
      return { isValid: false, errorMessage: "Data não existe no calendário" };
    }

    const age = calculateAge(year, month, day);
    if (age < 18) {
      return { isValid: false, errorMessage: "Você deve ter pelo menos 18 anos" };
    }

    return { isValid: true, year, month, day, age };
  };

  const formatDateToISO = (year: number, month: number, day: number): string => {
    // Manual formatting to ensure exact date without timezone conversion
    const yearStr = year.toString();
    const monthStr = month.toString().padStart(2, '0');
    const dayStr = day.toString().padStart(2, '0');
    return `${yearStr}-${monthStr}-${dayStr}`;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const formatted = formatDate(rawValue);
    
    setDisplayValue(formatted);
    
    if (formatted.length === 10) {
      const validation = validateAndParseDate(formatted);
      if (validation.isValid && validation.year && validation.month && validation.day && validation.age !== undefined) {
        const isoDate = formatDateToISO(validation.year, validation.month, validation.day);
        onChange(isoDate, validation.age);
      } else {
        onChange(null, 0);
      }
    } else {
      onChange(null, 0);
    }
  };

  const handleBlur = () => {
    if (displayValue && displayValue.length > 0) {
      const validation = validateAndParseDate(displayValue);
      if (!validation.isValid) {
        toast.error("Data Inválida", {
          description: validation.errorMessage || "Por favor, insira uma data válida no formato DD/MM/YYYY"
        });
        // Focus back on the input after a small delay
        setTimeout(() => {
          inputRef.current?.focus();
        }, 100);
      }
    }
  };

  const validation = displayValue.length === 10 ? validateAndParseDate(displayValue) : { isValid: true };
  const showAge = validation.isValid && validation.age !== undefined;

  return (
    <div className="space-y-2">
      <Label htmlFor="birthdate" className="text-white">
        {label}
      </Label>
      <Input
        ref={inputRef}
        id="birthdate"
        value={displayValue}
        onChange={handleInputChange}
        onBlur={handleBlur}
        placeholder="DD/MM/AAAA"
        maxLength={10}
        inputMode="numeric"
        className="bg-black/40 border-white/20 text-white placeholder:text-white/50"
      />
      {error && (
        <p className="text-red-400 text-sm">{error}</p>
      )}
      {showAge && validation.age && (
        <p className="text-luxury-gold text-sm">Idade: {validation.age} anos</p>
      )}
    </div>
  );
};
